import React, { useEffect } from "react"
import PropTypes from "prop-types"
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom"
import { routes } from "router"
import Cable from "components/Cable"
import FirstLoading from "modules/loader-watchers/FirstLoading"

import useLoaderWatchers from "modules/loader-watchers/hooks/useLoaderWatchers"
import useAuthRedirectedToken from "hooks/useAuthRedirectedTokens"

import { useDispatch, useSelector } from "react-redux"
import { cleanUser, getUser, platformConnectConfirm } from "store/user"
import { oldRoutes } from "./old-routes"
import { VERIFICATION_STATUS } from "layoutsV2/pro/PersonalInfoLayout/constants"
import GuestEmailVerifyModal from "components/GuestEmailVerifyModal"

// Matches /client/booking_trips/:uuid (no trailing slash or sub-paths)
const BOOKING_TRIP_PATH_RE = /\/client\/booking_trips\/([^/]+)$/

const PrivateRoutes = ({ accessRole = null, accessRoles = null }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  useAuthRedirectedToken(platformConnectConfirm)
  useLoaderWatchers({ watchers: "user" })
  const loggedIn = useSelector((state) => state.auth.loggedIn)
  const { id, role } = useSelector((store) => store.user)
  const guestAccess = useSelector((state) => state.guestAccess)
  const from = useLocation()

  useEffect(() => {
    if (loggedIn && !id) {
      dispatch(getUser()).then(({ role, guide }) => {
        if ((role === "guide_user" || role === "staff_user") && guide?.verification_status === VERIFICATION_STATUS.IN_PROGRESS) {
          navigate(routes.proPersonalInfoRootPath())
        }
      })
    }
    return () => !loggedIn && dispatch(cleanUser())
  }, [loggedIn, id]) //eslint-disable-line

  if (loggedIn && role) {
    const allowedRoles = accessRoles || (accessRole ? [accessRole] : null)
    if (allowedRoles && !allowedRoles.includes(role)) {
      return <Navigate to={oldRoutes.rootPath()} replace />
    }
  }

  // Guest (logged-out) user accessing a booking trip path via email link
  if (!loggedIn) {
    const bookingMatch = BOOKING_TRIP_PATH_RE.exec(from.pathname)
    if (bookingMatch) {
      const uuid = bookingMatch[1]
      if (guestAccess.authorized && guestAccess.bookingUuid === uuid) {
        // Email verified — grant access to this specific booking only.
        // All other PrivateRoutes-protected pages remain inaccessible (no loggedIn = false fallback).
        return <Outlet />
      }
      // Show email verification modal instead of redirecting to sign-in
      return <GuestEmailVerifyModal bookingUuid={uuid} />
    }
  }

  return loggedIn ? (
    <FirstLoading className="vh-100">
      {id ? (
        <Cable>
          <Outlet />
        </Cable>
      ) : (
        "User not found"
      )}
    </FirstLoading>
  ) : (
    <Navigate to={routes.signInV2()} state={{ from }} replace />
  )
}

PrivateRoutes.propTypes = {
  accessRole: PropTypes.string,
  accessRoles: PropTypes.arrayOf(PropTypes.string)
}

export default PrivateRoutes
